import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { FileText, Upload, Loader2, X, Trash2, Eye, FolderOpen, Download } from "lucide-react";
import { toast } from "sonner";

const ENTITIES = ["VİLAYET AWM ŞUBESİ", "VİLAYET MERKEZ ŞUBE", "EYALET ALTIN"];

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function Evraklar() {
  const qc = useQueryClient();
  const [entity, setEntity] = useState(ENTITIES[0]);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["documents", entity],
    queryFn: async () => (await api.get(`/documents?entity=${encodeURIComponent(entity)}`)).data,
  });

  const uploadM = useMutation({
    mutationFn: ({ fd, title }) =>
      api.post(`/documents?entity=${encodeURIComponent(entity)}&title=${encodeURIComponent(title)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success("Evrak yüklendi");
      setFile(null);
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Yükleme başarısız"),
  });

  const delM = useMutation({
    mutationFn: (id) => api.delete(`/documents/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Evrak silindi"); },
    onError: () => toast.error("Silinemedi"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!file) return toast.error("PDF dosyası seçin");
    const fd = new FormData();
    fd.append("file", file);
    uploadM.mutate({ fd, title: title.trim() || file.name.replace(/\.[^.]+$/, "") });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <FolderOpen className="w-8 h-8 text-blue-400" /> Şirket Evrakları
        </h1>
        <p className="text-slate-400 text-sm mt-1">Taranmış evrakları PDF olarak yükleyin, önizleyin ve şirket bazında arşivleyin.</p>
      </div>

      {/* Entity tabs */}
      <div className="flex flex-wrap gap-2" data-testid="entity-tabs">
        {ENTITIES.map((en) => (
          <button
            key={en}
            data-testid={`entity-tab-${en}`}
            onClick={() => setEntity(en)}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${
              entity === en
                ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/25"
                : "glass text-slate-300 hover:text-white border-transparent"
            }`}
          >
            {en}
          </button>
        ))}
      </div>

      {/* Upload */}
      <form onSubmit={submit} className="glass rounded-2xl p-5 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="doc-upload-form">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-widest text-slate-400">PDF Dosyası</label>
          <input
            ref={fileRef}
            data-testid="doc-file-input"
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files[0] || null)}
            className="mt-1.5 w-full text-sm text-slate-400 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-xs file:font-semibold hover:file:bg-blue-500 file:cursor-pointer"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs uppercase tracking-widest text-slate-400">Evrak Adı</label>
          <input
            data-testid="doc-title-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Örn. Vergi Levhası 2026"
            className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
          />
        </div>
        <button
          data-testid="doc-submit-button"
          type="submit"
          disabled={!file || uploadM.isPending}
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
        >
          {uploadM.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Yükle
        </button>
      </form>

      {/* List */}
      <div className="glass rounded-2xl overflow-hidden" data-testid="doc-list">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
          <div className="col-span-6">Evrak</div>
          <div className="col-span-2">Boyut</div>
          <div className="col-span-2">Yüklenme</div>
          <div className="col-span-2 text-right">İşlem</div>
        </div>
        <div className="divide-y divide-slate-800/60">
          {isLoading && <div className="py-12 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!isLoading && docs.length === 0 && (
            <div className="text-slate-500 text-sm py-16 text-center flex flex-col items-center gap-2">
              <FileText className="w-6 h-6" /> {entity} için henüz evrak yok.
            </div>
          )}
          {docs.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center px-5 py-4">
              <div className="md:col-span-6 flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-rose-500/15 text-rose-400 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">{d.entity}</div>
                </div>
              </div>
              <div className="md:col-span-2 text-xs text-slate-400 tabular">{fmtSize(d.size)}</div>
              <div className="md:col-span-2 text-xs text-slate-400 tabular">{fmtDate(d.created_at)}</div>
              <div className="md:col-span-2 flex items-center md:justify-end gap-2">
                <button
                  data-testid={`doc-preview-${d.id}`}
                  onClick={() => setPreview(d)}
                  className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 border border-blue-500/30 hover:border-blue-400/50 rounded-lg px-3 py-2 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" /> Önizle
                </button>
                <a
                  data-testid={`doc-download-${d.id}`}
                  href={`${API}/documents/${d.id}/download`}
                  className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400/50 rounded-lg px-3 py-2 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" /> İndir
                </a>
                <button
                  data-testid={`doc-delete-${d.id}`}
                  onClick={() => delM.mutate(d.id)}
                  disabled={delM.isPending}
                  className="text-slate-600 hover:text-rose-400 p-2 transition-colors"
                  title="Sil"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div
            data-testid="doc-preview-modal"
            className="glass rounded-2xl w-full max-w-4xl h-[88vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{preview.title}</div>
                <div className="text-xs text-slate-500">{preview.entity} · {fmtDate(preview.created_at)}</div>
              </div>
              <button data-testid="doc-preview-close" onClick={() => setPreview(null)} className="text-slate-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              data-testid="doc-preview-frame"
              src={`${API}/files/${preview.path}`}
              title={preview.title}
              className="flex-1 w-full bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}
